<template>
  <div class="inequality-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan" v-for="(val, label) in stats" :key="label">
             <label>{{ label }}</label>
             <span class="val">{{ val.toFixed(2) }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <!-- A. 均值不等式 (AM-GM) 模式 -->
          <g v-if="mode === 'AM-GM'" transform="translate(50, 200)">
             <!-- 半圆模型 -->
             <path :d="`M 0,0 A ${radius},${radius} 0 0,1 ${radius*2},0`" fill="rgba(6, 182, 212, 0.1)" stroke="#334155" stroke-dasharray="4,2" />
             <line x1="0" y1="0" :x2="radius*2" y2="0" stroke="#1e293b" stroke-width="2" />
             
             <!-- a 部分 -->
             <line x1="0" y1="0" :x2="a_disp" y2="0" stroke="#f43f5e" stroke-width="4" />
             <text :x="a_disp/2" y="15" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="900">a</text>
             <!-- b 部分 -->
             <line :x1="a_disp" y1="0" :x2="radius*2" y2="0" stroke="#0ea5e9" stroke-width="4" />
             <text :x="a_disp + (radius*2-a_disp)/2" y="15" text-anchor="middle" fill="#0ea5e9" font-size="10" font-weight="900">b</text>

             <!-- 算术平均线 (半径) -->
             <line :x1="radius" y1="0" :x2="radius" :y2="-radius" stroke="#fbbf24" stroke-width="3" stroke-dasharray="2,1" />
             <text :x="radius+5" :y="-radius/2" fill="#fbbf24" font-size="10" font-weight="900">算术平均 AM = (a+b)/2</text>
             
             <!-- 几何平均线 (垂线) -->
             <line :x1="a_disp" y1="0" :x2="a_disp" :y2="-gm_h" stroke="#10b981" stroke-width="3" />
             <text :x="a_disp-5" :y="-gm_h/2" text-anchor="end" fill="#10b981" font-size="10" font-weight="900">几何平均 GM = √(ab)</text>
          </g>

          <!-- B. 柯西不等式 (Cauchy) 模式 - 向量夹角展示 -->
          <g v-else-if="mode === 'Cauchy'" transform="translate(200, 120)">
             <line x1="-120" y1="0" x2="120" y2="0" stroke="#334155" stroke-dasharray="2,2" />
             <line x1="0" y1="-100" x2="0" y2="100" stroke="#334155" stroke-dasharray="2,2" />
             <!-- 向量 u -->
             <line x1="0" y1="0" :x2="a*1.2" :y2="-b*1.2" stroke="#f43f5e" stroke-width="4" marker-end="url(#arr-u)" />
             <text :x="a*1.2" :y="-b*1.2-5" fill="#f43f5e" font-size="10" font-weight="900">向量 u(a,b)</text>
             <!-- 向量 v (固定轴) -->
             <line x1="0" y1="0" :x2="100" y2="0" stroke="#0ea5e9" stroke-width="4" marker-end="url(#arr-v)" />
             <text x="105" y="15" fill="#0ea5e9" font-size="10" font-weight="900">向量 v(1,0)</text>
             
             <!-- 投影演示 -->
             <line :x1="a*1.2" :y1="-b*1.2" :x2="a*1.2" y2="0" stroke="#94a3b8" stroke-dasharray="4,2" />
             <text :x="a*0.6" y="15" text-anchor="middle" fill="#fbbf24" font-size="11" font-weight="bold">|u·v| ≤ |u||v| 成立</text>
          </g>

          <!-- C. 琴生不等式 (Jensen) 模式 - 函数凸性展示 -->
          <g v-else-if="mode === 'Jensen'" transform="translate(50, 200)">
             <path d="M 0,0 Q 150,-240 300,-40" fill="none" stroke="#64748b" stroke-width="3" />
             <!-- 割线 -->
             <line :x1="a*1.5" :y1="-getJensenY(a)" :x2="b*1.5" :y2="-getJensenY(b)" stroke="#fbbf24" stroke-width="2" />
             <!-- 中点对比 -->
             <circle :cx="(a+b)*0.75" :cy="-getJensenY((a+b)/2)" r="5" fill="#10b981" />
             <circle :cx="(a+b)*0.75" :cy="-(getJensenY(a)+getJensenY(b))/2" r="5" fill="#f43f5e" />
             <text :x="(a+b)*0.75 + 10" :cy="-(getJensenY(a)+getJensenY(b))/2 + 10" fill="#64748b" font-size="9">割线中点高于函数中点</text>
          </g>

          <defs>
             <marker id="arr-u" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
             <marker id="arr-v" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#0ea5e9" /></marker>
          </defs>
        </svg>

        <div class="physics-caption">
           核心结论：{{ activeDesc }}
        </div>
      </div>

      <div class="compact-controls">
        <!-- 控制面板重构：选项一行 -->
        <div class="control-card">
           <div class="flex-row items-center justify-between mb-4">
              <label class="s-label">知识点切换选择 (已激活全部模式)：</label>
              <div class="btn-group-row">
                 <el-button v-for="m in [{k:'AM-GM',n:'均值'}, {k:'Cauchy',n:'柯西'}, {k:'Jensen',n:'琴生'}]" :key="m.k"
                            :type="mode === m.k ? 'primary' : 'info'" size="mini" plain
                            @click="mode = m.k">{{ m.n }}</el-button>
              </div>
           </div>
           
           <div class="flex-row gap-6">
              <div class="flex-1">
                 <label class="s-label">调节参数 a (数值 A)</label>
                 <el-slider v-model="a" :min="10" :max="90" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">调节参数 b (数值 B)</label>
                 <el-slider v-model="b" :min="10" :max="90" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-discovery" v-latex>
           <i class="el-icon-collection"></i>
           <p><strong>实战物理/数学：</strong>数学不等式不仅是计算，更是几何直觉。<strong>观察：</strong>点击 Cauchy 模式，观察向量点积与长度乘积的关系；点击 Jensen 模式，观察割线与曲线中点的包格关系。无论滑块如何移动，**红色目标永远不会超过黄色上限**。这正是数学逻辑的绝对严密性。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'InequalityViz',
  data() {
    return {
      mode: 'AM-GM',
      a: 30,
      b: 70
    }
  },
  computed: {
    activeFormula() {
       if (this.mode === 'AM-GM') return '(a+b)/2 ≥ √(ab)';
       if (this.mode === 'Cauchy') return '|u·v| ≤ |u||v|';
       return 'f(Σλi xi) ≤ Σλi f(xi)';
    },
    activeDesc() {
       if (this.mode === 'AM-GM') return '几何平均值(高度) 永不大于 算术平均值(半径)；当 a=b 时等号成立。';
       if (this.mode === 'Cauchy') return '两个向量的数量积(点积) 永不大于 其模长的乘积。';
       return '凹函数的割线中心点，永远在曲线中心点的上方。';
    },
    stats() {
       if (this.mode === 'AM-GM') return { '算术平均': (this.a + this.b) / 2, '几何平均': Math.sqrt(this.a * this.b) };
       if (this.mode === 'Cauchy') return { '左式 |u·v|': this.a, '右式 |u||v|': Math.sqrt(this.a**2 + this.b**2) };
       return { '割线中点': 0.5 * (this.getJensenY(this.a) + this.getJensenY(this.b)), '曲线中点': this.getJensenY((this.a+this.b)/2) };
    },
    radius() { return (this.a + this.b) * 1.5; },
    a_disp() { return this.a * 3; },
    gm_h() { return Math.sqrt(this.a_disp * (this.radius*2 - this.a_disp)); }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       if (kp.includes('均值')) this.mode = 'AM-GM';
       else if (kp.includes('柯西')) this.mode = 'Cauchy';
       else if (kp.includes('詹森') || kp.includes('琴生')) this.mode = 'Jensen';
       else this.mode = 'AM-GM';
    },
    getJensenY(x) {
       // Mock convex function parabola
       const t = (x - 10) / 80;
       return 100 * (t - Math.pow(t, 2) * 1.5 + 1); 
    }
  }
}
</script>

<style scoped lang="scss">
.inequality-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fefce8; color: #854d0e; border: 1px solid #fef08a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 10px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 12px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.btn-group-row { display: flex; gap: 8px; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; display: block; }
.discovery-card-discovery { background: #eff6ff; padding: 12px 16px; border-radius: 10px; border: 1px solid #bfdbfe; display: flex; align-items: center; gap: 12px; i { color: #1e40af; font-size: 1.3rem; } p { flex: 1; margin: 0; font-size: 11.5px; color: #1e3a8a; line-height: 1.6; strong { color: #1e3a8a; font-weight: 900; } } }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .justify-between { justify-content: space-between; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; } .mb-4 { margin-bottom: 12px; }
</style>
